import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Bell } from "lucide-react";
import { t, useLang } from "../../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const headers = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("funz_token")}` },
  withCredentials: true,
});

export const NotifBell = () => {
  const lang = useLang();
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const ref = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/notifications/mine`, headers());
      setItems(data.notifications);
      setUnread(data.unread);
    } catch {
      /* silent */
    }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => {
    const close = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  const openPanel = async () => {
    const next = !open;
    setOpen(next);
    if (next && unread > 0) {
      setUnread(0);
      setItems((list) => list.map((i) => ({ ...i, read: true })));
      await axios.post(`${API}/notifications/read-all`, {}, headers()).catch(() => {});
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        data-testid="notif-bell"
        onClick={openPanel}
        aria-label={t(lang, "notif_title")}
        className="relative flex h-9 w-9 items-center justify-center rounded-full border border-white/10 bg-white/5 text-slate-300 transition-colors hover:border-[#22C55E]/60 hover:text-white"
      >
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span data-testid="notif-badge" className="absolute -right-1 -top-1 flex h-4.5 min-w-[18px] items-center justify-center rounded-full bg-[#F97316] px-1 font-jbmono text-[9px] font-bold text-white" style={{ height: 18 }}>
            {unread}
          </span>
        )}
      </button>
      {open && (
        <div
          data-testid="notif-panel"
          className="absolute right-0 top-12 z-50 w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-white/10 bg-[#111827]/95 p-3 shadow-2xl backdrop-blur-2xl rtl:left-0 rtl:right-auto"
        >
          <p className="px-2 pb-2 font-jbmono text-[10px] uppercase tracking-[0.25em] text-slate-500">
            {t(lang, "notif_title")}
          </p>
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {items.length === 0 ? (
              <p className="px-2 py-6 text-center text-sm text-slate-500">
                {t(lang, "notif_empty")}
              </p>
            ) : (
              items.map((n) => (
                <div key={n.id} data-testid={`notif-${n.id.slice(-6)}`} className="rounded-xl border border-white/5 bg-white/[0.03] px-3.5 py-3">
                  <p className="text-sm font-bold text-white">{n.title}</p>
                  <p className="mt-0.5 text-xs leading-relaxed text-slate-400">{n.body}</p>
                  <p className="mt-1 font-jbmono text-[9px] text-slate-600">
                    {String(n.created_at).slice(0, 16).replace("T", " ")}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
